import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, ArrowRight, CheckCircle2, ShieldCheck, AlertTriangle, RefreshCw, Inbox } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import PhoneInput from "@/components/PhoneInput";
import { otpApiError } from "@/components/OtpModal";

const RESEND_COOLDOWN_SECONDS = 60;

const ForgotPassword = () => {
  const { t } = useI18n();
  const [mode, setMode] = useState("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState({ dialCode: "+1", number: "" });
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [step, setStep] = useState(1); // phone mode: 1=enter phone, 2=code+new password
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false); // email link sent
  const [resetDone, setResetDone] = useState(false); // sms reset done
  const [error, setError] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [resendMsg, setResendMsg] = useState("");
  const fullPhone = `${phone.dialCode}${phone.number}`;

  // Countdown for resend cooldown
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((n) => Math.max(0, n - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const submitEmail = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      setResendIn(RESEND_COOLDOWN_SECONDS);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const resendEmail = async () => {
    if (resendIn > 0 || !email) return;
    setResendMsg("");
    try {
      await api.post("/auth/forgot-password", { email });
      setResendMsg("Correo reenviado. Revisa tu bandeja y también la carpeta de spam.");
      setResendIn(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setResendMsg(formatApiError(err));
    }
  };

  const sendSmsCode = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/auth/forgot-password-sms", { phone: fullPhone });
      setStep(2);
    } catch (err) { setError(otpApiError(err, t)); }
    finally { setLoading(false); }
  };

  const submitSmsReset = async (e) => {
    e.preventDefault();
    if (password !== password2) { setError(t.fields.passwordsDoNotMatch); return; }
    setLoading(true); setError("");
    try {
      await api.post("/auth/reset-password-sms", { phone: fullPhone, code: code.trim(), password });
      setResetDone(true);
    } catch (err) { setError(otpApiError(err, t)); }
    finally { setLoading(false); }
  };

  const switchMode = (m) => { setMode(m); setError(""); setStep(1); setCode(""); };

  if (sent || resetDone) {
    return (
      <section className="min-h-[80vh] bg-cream flex items-center justify-center py-16 px-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-gray-200 p-8 md:p-10 text-center">
          <CheckCircle2 className="text-orange mx-auto" size={52} />
          <h1 className="font-display text-3xl text-teal-deep mt-4 leading-tight" data-testid={resetDone ? "forgot-sms-done" : "forgot-sent"}>
            {resetDone ? t.otp.resetDone : t.forgot.sentTitle}
          </h1>
          <p className="text-teal-soft mt-3 leading-relaxed">{resetDone ? t.otp.resetDoneBody : t.forgot.sentBody}</p>

          {sent && (
            <>
              {/* Anti-spam reminder — prominent */}
              <div
                className="mt-6 rounded-2xl bg-orange/10 border-2 border-orange/30 text-left p-4 md:p-5"
                data-testid="forgot-spam-notice"
              >
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-full bg-orange text-white flex items-center justify-center shrink-0">
                    <AlertTriangle size={18} />
                  </span>
                  <div>
                    <p className="font-display text-lg text-teal-deep leading-tight">
                      ¿No lo ves? Revisa <span className="text-orange">Spam</span> y <span className="text-orange">Promociones</span>
                    </p>
                    <p className="text-sm text-teal-soft mt-1 leading-relaxed">
                      Los correos automáticos a veces caen ahí. Búscanos como
                      <strong className="text-teal-deep"> noreply@redras.app</strong> o el asunto
                      <em> "Restablecer tu contraseña"</em>. Marca como "No es spam" para futuros correos.
                    </p>
                  </div>
                </div>
              </div>

              {/* Resend button with cooldown */}
              <div className="mt-5 flex flex-col items-center gap-2" data-testid="forgot-resend-wrap">
                <button
                  type="button"
                  onClick={resendEmail}
                  disabled={resendIn > 0}
                  className={`inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider rounded-full px-5 py-2.5 transition-colors ${
                    resendIn > 0
                      ? "bg-cream text-teal-soft cursor-not-allowed"
                      : "bg-white border-2 border-orange text-orange hover:bg-orange hover:text-white"
                  }`}
                  data-testid="forgot-resend-btn"
                >
                  <RefreshCw size={13} />
                  {resendIn > 0 ? `Enviar de nuevo en ${resendIn} s` : "Enviar de nuevo"}
                </button>
                {resendMsg && (
                  <p className="text-xs text-teal-deep" data-testid="forgot-resend-msg">
                    <Inbox size={11} className="inline mr-1" /> {resendMsg}
                  </p>
                )}
              </div>
            </>
          )}

          <Link to="/login" className="btn-orange mt-6 inline-flex">
            {t.forgot.backToLogin} <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-[80vh] bg-cream flex items-center justify-center py-16">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-gray-200 p-8 md:p-10">
        <div className="text-center">
          <span className="w-12 h-12 rounded-full bg-orange/15 text-orange mx-auto flex items-center justify-center">
            {mode === "email" ? <Mail size={20} /> : <ShieldCheck size={20} />}
          </span>
          <p className="eyebrow text-orange mt-4">{t.forgot.eyebrow}</p>
          <h1 className="font-display text-3xl text-teal-deep mt-1 leading-tight">{t.forgot.title}</h1>
          <p className="text-teal-soft mt-3 text-sm">{mode === "email" ? t.forgot.subtitle : t.otp.forgotSmsSub}</p>
        </div>

        <div className="grid grid-cols-2 gap-1 bg-cream rounded-xl p-1 mt-6" data-testid="forgot-mode-toggle">
          <button
            type="button" onClick={() => switchMode("email")}
            className={`inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${mode === "email" ? "bg-white text-teal-deep shadow-sm" : "text-teal-soft hover:text-teal-deep"}`}
            data-testid="forgot-mode-email"
          >
            <Mail size={13} /> {t.otp.emailOpt}
          </button>
          <button
            type="button" onClick={() => switchMode("phone")}
            className={`inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${mode === "phone" ? "bg-white text-teal-deep shadow-sm" : "text-teal-soft hover:text-teal-deep"}`}
            data-testid="forgot-mode-phone"
          >
            <Phone size={13} /> {t.otp.phoneOpt}
          </button>
        </div>

        {mode === "email" ? (
          <form onSubmit={submitEmail} className="mt-6 space-y-4" data-testid="forgot-form">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.auth.email}</label>
              <input
                type="email" required autoFocus className="field-input mt-1"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com" data-testid="forgot-email"
              />
            </div>
            {error && <p className="text-red-600 text-sm" data-testid="forgot-error">{error}</p>}
            <button type="submit" disabled={loading} className="btn-orange w-full justify-center" data-testid="forgot-submit">
              {loading ? "…" : t.forgot.sendLink} <ArrowRight size={14} />
            </button>
          </form>
        ) : step === 1 ? (
          <form onSubmit={sendSmsCode} className="mt-6 space-y-4" data-testid="forgot-sms-form">
            <PhoneInput
              label={t.fields.phone}
              value={phone}
              onChange={setPhone}
            />
            {error && <p className="text-red-600 text-sm" data-testid="forgot-sms-error">{error}</p>}
            <button type="submit" disabled={loading || !phone.number} className="btn-orange w-full justify-center" data-testid="forgot-sms-send">
              {loading ? "…" : t.otp.sendCode} <ArrowRight size={14} />
            </button>
          </form>
        ) : (
          <form onSubmit={submitSmsReset} className="mt-6 space-y-4" data-testid="forgot-sms-reset-form">
            <p className="text-sm text-teal-soft">
              {t.otp.sub} <strong className="text-teal-deep">{fullPhone}</strong>.
            </p>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.otp.codeLabel}</label>
              <input
                autoFocus inputMode="numeric" pattern="[0-9]*" maxLength={6}
                className="field-input mt-1 text-center text-2xl tracking-[0.5em] font-bold"
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                data-testid="forgot-sms-code"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.fields.createPassword}</label>
              <input type="password" required minLength={6} className="field-input mt-1" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="forgot-sms-password" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-teal">{t.fields.confirmPassword}</label>
              <input type="password" required minLength={6} className="field-input mt-1" value={password2} onChange={(e) => setPassword2(e.target.value)} data-testid="forgot-sms-password2" />
              {password2 && password !== password2 && (
                <p className="text-xs text-red-600 mt-1">{t.fields.passwordsDoNotMatch}</p>
              )}
            </div>
            {error && <p className="text-red-600 text-sm" data-testid="forgot-sms-error">{error}</p>}
            <button type="submit" disabled={loading || code.length < 4} className="btn-orange w-full justify-center" data-testid="forgot-sms-reset-btn">
              {loading ? "…" : t.otp.resetBtn}
            </button>
            <button type="button" onClick={() => { setStep(1); setError(""); }} className="w-full text-xs font-bold text-teal hover:text-orange transition-colors" data-testid="forgot-sms-back">
              ← {t.registerAccount?.back || t.auth.back || "Volver"}
            </button>
          </form>
        )}

        <p className="text-center text-sm text-teal-soft mt-6">
          <Link to="/login" className="text-orange font-bold hover:underline">
            ← {t.forgot.backToLogin}
          </Link>
        </p>
      </div>
    </section>
  );
};

export default ForgotPassword;
